import { useEffect, useLayoutEffect, useState } from 'react'

type TableOfContents = {
  id: string
  text: string | null
  level: string
  children: TableOfContents[]
}

export function useTableOfContents(containerId = 'content') {
  const [toc, setToc] = useState<TableOfContents[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)

  useLayoutEffect(() => {
    let container: HTMLElement | null
    let headingElements: Element[] = []
    let observer: IntersectionObserver | null = null

    setTimeout(() => {
      container = document.getElementById(containerId)
      if (!container) return

      headingElements = Array.from(container.querySelectorAll('h2, h3')).filter(
        el => {
          // Only include headings NOT inside a div (other than the container itself)
          const closestDiv = el.closest('div')
          return !closestDiv || closestDiv === container
        }
      )

      const structured: TableOfContents[] = []
      const idCounts: Record<string, number> = {}

      headingElements.forEach(el => {
        if (!el.id) {
          const baseId =
            el.textContent
              ?.toLowerCase()
              .replace(/\s+/g, '-')
              .replace(/[^\w-]+/g, '') || 'heading'
          idCounts[baseId] = (idCounts[baseId] || 0) + 1
          const uniqueId =
            idCounts[baseId] > 1 ? `${baseId}-${idCounts[baseId]}` : baseId
          el.id = uniqueId
          el.classList.add('scroll-mt-[130px]')
        }

        const node = {
          id: el.id,
          text: el.textContent,
          level: el.tagName,
          children: []
        }

        if (el.tagName === 'H2') {
          structured.push({ ...node })
        } else if (el.tagName === 'H3' && structured.length > 0) {
          structured[structured.length - 1].children.push(node)
        }
      })

      setToc(structured)

      observer = new IntersectionObserver(
        entries => {
          const visible = entries
            .filter(entry => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)
          if (visible.length > 0) {
            setActiveId(visible[0].target.id)
          }
        },
        {
          rootMargin: '-140px 0px -80% 0px',
          threshold: [0, 0]
        }
      )

      headingElements.forEach(el => {
        observer!.observe(el)
      })
    }, 300)

    return () => {
      if (observer && headingElements.length > 0) {
        headingElements.forEach(el => observer!.unobserve(el))
        observer.disconnect()
      }
    }
  }, [containerId])

  useEffect(() => {
    if (toc.length === 0) return
    if (window.location.hash) {
      const id = window.location.hash.slice(1)
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
  }, [toc])

  return { toc, activeId }
}
